// Minimal metrics utilities used by ModernDisciplinePanel

export function computeDisciplineMetrics({ goals = [], timers = [], xp = 0, streak = 0, now = Date.now() } = {}) {
  const safeGoals = Array.isArray(goals) ? goals : [];
  const safeTimers = Array.isArray(timers) ? timers : [];

  const completedGoals = safeGoals.filter(g => g.completed).length;
  const focusMinutes = safeTimers.filter(t => t?.isCompleted).reduce((sum, t) => sum + (t.duration || 0), 0);
  const focusHours = Math.round((focusMinutes / 60) * 10) / 10;
  const sessionsLast7Days = safeTimers.filter(t => {
    const end = new Date(t.endedAt || t.updatedAt || t.createdAt || now).getTime();
    return now - end <= 7 * 24 * 3600 * 1000;
  }).length;

  const today = new Date(now).toDateString();
  const focusMinutesToday = safeTimers.filter(t => new Date(t.updatedAt || t.createdAt || now).toDateString() === today)
    .reduce((sum, t) => sum + (t.duration || 0), 0);

  // simple level calc: every 1000 xp is a level
  const currentLevel = Math.floor(xp / 1000) + 1;
  const xpIntoLevel = Math.floor(xp % 1000);
  const xpToNext = 1000 - xpIntoLevel;
  const xpVelocityPerDay = Math.max(0, Math.round((xp / Math.max(1, (xp === 0 ? 1 : 7))) )); // placeholder

  // weekly distribution placeholder (7 numbers)
  const weeklyFocus = Array.from({ length: 7 }, (_, i) => {
    // mock distribution based on timers
    return Math.min(180, Math.round((focusMinutes / 7) + (i * 3)));
  });

  // category breakdown from goals
  const categoryMap = new Map();
  safeGoals.forEach(g => {
    const cat = g.category || 'general';
    const mins = g.duration || 0;
    categoryMap.set(cat, (categoryMap.get(cat) || 0) + mins);
  });
  const totalCat = Array.from(categoryMap.values()).reduce((a, b) => a + b, 0) || 1;
  const categoryBreakdown = Array.from(categoryMap.entries()).map(([category, minutes]) => ({
    category,
    minutes,
    percent: Math.round((minutes / totalCat) * 100)
  }));

  // achievements placeholder
  const achievements = [];
  if (completedGoals >= 1) achievements.push({ id: 'ach-1', type: 'Completed Goals', value: completedGoals });
  if (focusHours >= 1) achievements.push({ id: 'ach-2', type: 'Focus Hours', value: focusHours });

  const efficiencyScore = Math.min(100, Math.round((focusMinutes / Math.max(1, safeTimers.length * 60)) * 100));

  // median/avg session length
  const lengths = safeTimers.map(t => t.duration || 0).sort((a, b) => a - b);
  const avgSessionMinutes = lengths.length ? Math.round(lengths.reduce((a, b) => a + b, 0) / lengths.length) : 0;
  const medianSessionMinutes = lengths.length ? (lengths[Math.floor(lengths.length / 2)] || 0) : 0;

  return {
    completedGoals,
    focusHours,
    sessionsLast7Days,
    focusMinutesToday,
    currentLevel,
    xpIntoLevel,
    xpToNext,
    xpVelocityPerDay,
    weeklyFocus,
    categoryBreakdown,
    achievements,
    efficiencyScore,
    streak,
    xp,
    avgSessionMinutes,
    medianSessionMinutes,
  };
}

export function buildSparkline(values = []) {
  // Build polyline points string for 0..160 x-axis and 0..40 y-axis
  const max = Math.max(1, ...values);
  const stepX = 160 / Math.max(1, values.length - 1);
  return values.map((v, i) => {
    const x = Math.round(i * stepX);
    const y = Math.round(40 - (v / max) * 36 - 2);
    return `${x},${y}`;
  }).join(' ');
}
